import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_USER_CLIENTS_LIST_QUERY_KEY } from '@/modules/common/queries/clients.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { DOCUMENTS_TREE_QK } from '@/modules/admin-dashboard/queries/documents-folders.ts';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  CreatePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkPostParams,
  DeletePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkDeleteParams,
  GetDocumentsV1UserDocumentsGetParams,
  GetPublicDocumentV1UserDocumentsPublicPublicDocumentIdGetParams,
  UpdateUserDocumentRequest,
  UploadUserDocumentRequest,
} from 'compliance-api';

export const DOCUMENTS_LIST_QUERY_KEY = 'vestra-compliance/documents-list';
//
export const useGetDocumentsListQuery = (
  payload: GetDocumentsV1UserDocumentsGetParams,
  disabled = false,
) => {
  const { page, ...data } = payload;

  return useInfiniteQuery({
    queryKey: [DOCUMENTS_LIST_QUERY_KEY, JSON.stringify(data), useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getDocumentsV1UserDocumentsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useUploadDocumentMutation = () => {
  return useMutation({
    mutationFn: async (payload: UploadUserDocumentRequest) => {
      const response = await withErrorHandling(
        api.v1.uploadDocumentV1UserDocumentsUploadPost(payload),
        {
          loading: 'Uploading document...',
          success: 'Document uploaded',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
    },
  });
};

export const useCreatePublicDocumentLinkMutation = ({
  onSuccess,
}: {
  onSuccess?: (public_document_id?: string) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (
      payload: CreatePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkPostParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.createPublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkPost(payload),
        {
          loading: 'Creating public document link...',
          success: 'Public document link created',
        },
      );
      return response.data;
    },
    onSuccess: data => {
      onSuccess && onSuccess(data?.document?.public_document_id);
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeletePublicDocumentLinkMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (
      payload: DeletePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkDeleteParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.deletePublicDocumentLinkV1UserDocumentsDocumentIdPublicLinkDelete(payload),
        {
          loading: 'Deleting public document link...',
          success: 'Public document link deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useGetPublicDocumentByIdQuery = (
  payload: GetPublicDocumentV1UserDocumentsPublicPublicDocumentIdGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [DOCUMENTS_LIST_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPublicDocumentV1UserDocumentsPublicPublicDocumentIdGet(
        payload,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useUpdateDocumentMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateUserDocumentRequest) => {
      const response = await withErrorHandling(api.v1.updateDocumentV1UserDocumentsPatch(payload), {
        loading: 'Updating document...',
        success: 'Document updated',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeleteDocumentMutation = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteDocumentV1UserDocumentsDocumentIdDelete({ documentId: id }),
        {
          loading: 'Archiving document...',
          success: 'Document archived',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useRestoreDocumentMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.deleteDocumentV1UserDocumentsDocumentIdDelete({ documentId: id }),
        {
          loading: 'Restoring document...',
          success: 'Document restored',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};
